import { NavLink, Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Moon, Sun, LogOut, Plus, Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useData } from '../store/data';
import { pendingDueCount } from '../features/planned/plannedEntries';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ZONES, SECONDARY } from './nav';
import { useEntrySheet } from '../features/entry/useEntrySheet';
import QuotaBanner from './QuotaBanner';
import UpdateBanner from './UpdateBanner';

interface Props {
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  onSignOut: () => void;
  userName?: string | null;
}

const linkCls = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
    isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
  );

/** Số khoản dự kiến đã QUÁ HẠN chưa xử lý — hiện thành huy hiệu cạnh mục Dự kiến. */
const useLateBadge = (): number => {
  const d = useData(s => s.data);
  return pendingDueCount({ plannedExpenses: d.plannedExpenses, plannedIncomes: d.plannedIncomes, plannedPayments: d.plannedPayments, recurringTemplates: d.recurringTemplates }).late;
};
const Badge = ({ n }: { n: number }) => n > 0 ? <span className="num ml-auto rounded-full bg-warn px-1.5 text-xs font-semibold text-white" aria-label={`${n} khoản quá hạn`}>{n}</span> : null;

export default function Shell({ theme, onToggleTheme, onSignOut, userName }: Props) {
  const openNew = useEntrySheet(s => s.openNew);
  const [menu, setMenu] = useState(false);
  const [fabHidden, setFabHidden] = useState(false);
  const late = useLateBadge();

  // Phím tắt: N = ghi nhanh (bỏ qua khi đang gõ vào ô nhập)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.metaKey || e.ctrlKey || e.altKey || t.closest('input, textarea, select, [role="dialog"], [contenteditable]')) return;
      if (e.key === 'n' || e.key === 'N') { e.preventDefault(); openNew(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openNew]);

  // Nút ghi nhanh nổi ẩn khi cuộn xuống (khỏi che số tiền ở dòng cuối), hiện lại khi cuộn lên
  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > last + 8 && y > 120) setFabHidden(true);
      else if (y < last - 8 || y < 120) setFabHidden(false);
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen md:grid md:grid-cols-[15rem_1fr]">
      {/* Thanh bên — máy tính */}
      <aside className="sticky top-0 hidden h-screen flex-col border-r border-border bg-card px-3 py-5 md:flex">
        <div className="mb-6 flex items-center gap-2 px-3">
          <span className="h-2.5 w-2.5 rounded-full bg-primary" aria-hidden />
          <span className="text-lg font-semibold tracking-tight">FinTrack</span>
        </div>
        <Button className="mb-4 w-full justify-between" onClick={() => openNew()}>
          <span className="flex items-center gap-2"><Plus size={16} aria-hidden /> Ghi nhanh</span>
          <kbd className="rounded bg-primary-foreground/20 px-1.5 text-xs">N</kbd>
        </Button>
        <nav className="flex flex-col gap-1" aria-label="Điều hướng chính">
          {ZONES.map(z => (
            <NavLink key={z.to} to={z.to} end={z.to === '/v2'} className={linkCls}>
              <z.icon size={18} aria-hidden /> {z.label}{z.to === '/v2/du-kien' && <Badge n={late} />}
            </NavLink>
          ))}
        </nav>
        <div className="mt-6 border-t border-border pt-4">
          <p className="px-3 pb-1 text-sm font-semibold text-muted-foreground">Khác</p>
          <nav className="flex flex-col gap-1" aria-label="Điều hướng phụ">
            {SECONDARY.map(z => (
              <NavLink key={z.to} to={z.to} className={linkCls}>
                <z.icon size={18} aria-hidden /> {z.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="mt-auto flex flex-col gap-2 border-t border-border pt-4">
          <div className="flex items-center justify-between gap-2 px-3">
            <span className="truncate text-xs text-muted-foreground" title={userName ?? ''}>{userName}</span>
            <div className="flex">
              <Button variant="ghost" size="icon" onClick={onToggleTheme} aria-label={theme === 'dark' ? 'Chế độ sáng' : 'Chế độ tối'}>
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </Button>
              <Button variant="ghost" size="icon" onClick={onSignOut} aria-label="Đăng xuất"><LogOut size={16} /></Button>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-col pb-20 md:pb-0">
        {/* Thanh trên — điện thoại */}
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/90 px-4 py-2 backdrop-blur md:hidden">
          <span className="flex items-center gap-2 font-semibold tracking-tight">
            <Button variant="ghost" size="icon" onClick={() => setMenu(true)} aria-label="Mở menu" className="-ml-2"><Menu size={20} /></Button>
            <span className="h-2 w-2 rounded-full bg-primary" aria-hidden /> FinTrack
          </span>
          <div className="flex">
            <Button variant="ghost" size="icon" onClick={onToggleTheme} aria-label={theme === 'dark' ? 'Chế độ sáng' : 'Chế độ tối'}>
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <Button variant="ghost" size="icon" onClick={onSignOut} aria-label="Đăng xuất"><LogOut size={18} /></Button>
          </div>
        </header>

        <UpdateBanner />
        <QuotaBanner />
        <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 md:px-8 md:py-8">
          <Outlet />
        </main>
      </div>

      {/* Nút ghi nhanh nổi — điện thoại */}
      <Button onClick={() => openNew()} size="icon" aria-label="Ghi nhanh"
        className={cn('fixed bottom-20 right-4 z-40 h-14 w-14 rounded-full shadow-lg transition-transform duration-200 motion-reduce:transition-none md:hidden', fabHidden && 'translate-y-32')}
        tabIndex={fabHidden ? -1 : 0}>
        <Plus size={24} />
      </Button>

      {/* Thanh dưới — điện thoại */}
      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] md:hidden" aria-label="Điều hướng chính">
        {ZONES.map(z => (
          <NavLink
            key={z.to} to={z.to} end={z.to === '/v2'}
            className={({ isActive }) => cn('flex flex-col items-center gap-0.5 py-2 text-xs font-medium', isActive ? 'text-primary' : 'text-muted-foreground')}
          >
            <span className="relative"><z.icon size={20} aria-hidden />{z.to === '/v2/du-kien' && late > 0 && <span className="num absolute -right-2 -top-1.5 rounded-full bg-warn px-1 text-xs font-semibold leading-4 text-white">{late}</span>}</span>
            <span className="truncate">{z.label.replace(' & Nợ', '')}</span>
          </NavLink>
        ))}
      </nav>

      {/* Menu phụ — điện thoại (Kế hoạch, Báo cáo, Cài đặt) */}
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" className="w-72">
          <SheetHeader><SheetTitle>Menu</SheetTitle></SheetHeader>
          <nav className="mt-4 flex flex-col gap-1" aria-label="Menu phụ">
            {SECONDARY.map(z => (
              <NavLink key={z.to} to={z.to} onClick={() => setMenu(false)} className={linkCls}>
                <z.icon size={18} aria-hidden /> <span>{z.label}<span className="block text-xs font-normal text-muted-foreground">{z.question}</span></span>
              </NavLink>
            ))}
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );
}
